import { create } from 'zustand';
import apiClient from '../api/api';
import toast from 'react-hot-toast';
import { useAuthStore } from './useAuthStore';

const cloneMessagesArray = (messages) => {
  if (Array.isArray(messages)) {
    const next = messages.slice();
    Object.assign(next, messages);
    return next;
  }
  return [];
};

const computeUnread = (conversations) => {
  const unreadCounts = {};
  let total = 0;
  (conversations || []).forEach((conv) => {
    const count = Number(conv?.unreadCount || 0);
    unreadCounts[conv?._id] = count;
    total += count;
  });
  return { unreadCounts, totalUnreadCount: total };
};

export const useChatStore = create((set, get) => ({
  conversations: [],
  messagesByConversation: {},
  selectedConversationId: null,
  selectedConversation: null,
  onlineUsers: [],
  typingUsers: {},
  unreadCounts: {},
  totalUnreadCount: 0,
  messages: [],
  reactions: [],
  viewerCount: 0,
  liveStreams: [],
  currentLiveStream: null,
  isLiveStreamActive: false,
  currentStream: null,
  isLoading: false,
  giftAnimation: null,
  callStatus: 'idle',
  callType: null,
  caller: null,
  recipient: null,
  isIncoming: false,

  setConversations: (conversations) => {
    const safeList = Array.isArray(conversations) ? conversations : [];
    const { unreadCounts, totalUnreadCount } = computeUnread(safeList);
    const selectedConversationId = get().selectedConversationId;
    const selectedConversation = selectedConversationId
      ? safeList.find((conv) => conv?._id === selectedConversationId) || null
      : null;
    set({ conversations: safeList, unreadCounts, totalUnreadCount, selectedConversation });
  },

  setSelectedConversation: (conversationOrId) => {
    if (!conversationOrId) {
      set({ selectedConversationId: null, selectedConversation: null });
      return;
    }

    if (typeof conversationOrId === 'string') {
      const conversation = get().conversations.find((conv) => conv?._id === conversationOrId) || null;
      set({ selectedConversationId: conversationOrId, selectedConversation: conversation });
      return;
    }

    set({
      selectedConversationId: conversationOrId?._id || null,
      selectedConversation: conversationOrId || null,
    });
  },

  setMessages: (conversationId, messages) => {
    if (!conversationId) return;
    const list = Array.isArray(messages) ? messages : [];
    set((state) => {
      const nextLiveMessages = cloneMessagesArray(state.messages);
      nextLiveMessages[conversationId] = list;
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: list,
        },
        messages: nextLiveMessages,
      };
    });
  },

  addMessage: (conversationIdOrMessage, messageInput) => {
    if (messageInput) {
      const conversationId = conversationIdOrMessage;
      const message = messageInput;
      if (!conversationId || !message?._id) return;
      set((state) => {
        const currentMessages = state.messagesByConversation[conversationId] || [];
        if (currentMessages.some((m) => m?._id === message._id)) return state;

        const nextMessagesByConversation = {
          ...state.messagesByConversation,
          [conversationId]: [message, ...currentMessages],
        };

        const nextLiveMessages = cloneMessagesArray(state.messages);
        nextLiveMessages[conversationId] = nextMessagesByConversation[conversationId];

        const nextUnreadCounts = { ...state.unreadCounts };
        if (state.selectedConversationId !== conversationId) {
          nextUnreadCounts[conversationId] = Number(nextUnreadCounts[conversationId] || 0) + 1;
        }
        const totalUnreadCount = Object.values(nextUnreadCounts).reduce((sum, count) => sum + Number(count || 0), 0);

        const nextConversations = state.conversations.map((conv) =>
          conv?._id === conversationId ? { ...conv, lastMessage: message } : conv
        );

        return {
          messagesByConversation: nextMessagesByConversation,
          messages: nextLiveMessages,
          unreadCounts: nextUnreadCounts,
          totalUnreadCount,
          conversations: nextConversations,
        };
      });
      return;
    }

    const liveMessage = conversationIdOrMessage;
    if (!liveMessage) return;
    set((state) => {
      const nextMessages = Array.isArray(state.messages) ? [...state.messages, liveMessage] : [liveMessage];
      Object.assign(nextMessages, state.messages);
      return { messages: nextMessages };
    });
  },

  updateMessage: (conversationId, messageId, updates) => {
    if (!conversationId || !messageId) return;
    set((state) => {
      const currentMessages = state.messagesByConversation[conversationId] || [];
      if (currentMessages.length === 0) return state;
      const nextMessages = currentMessages.map((msg) =>
        msg?._id === messageId ? { ...msg, ...updates } : msg
      );
      const nextLiveMessages = cloneMessagesArray(state.messages);
      nextLiveMessages[conversationId] = nextMessages;
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
        messages: nextLiveMessages,
      };
    });
  },

  markConversationRead: (conversationId, readerId) => {
    if (!conversationId) return;
    set((state) => {
      const nextUnreadCounts = { ...state.unreadCounts, [conversationId]: 0 };
      const totalUnreadCount = Object.values(nextUnreadCounts).reduce((sum, count) => sum + Number(count || 0), 0);
      const nextMessages = (state.messagesByConversation[conversationId] || []).map((msg) =>
        msg?.sender?._id === readerId ? msg : { ...msg, readAt: msg.readAt || new Date().toISOString(), status: 'read' }
      );
      const nextLiveMessages = cloneMessagesArray(state.messages);
      nextLiveMessages[conversationId] = nextMessages;
      return {
        unreadCounts: nextUnreadCounts,
        totalUnreadCount,
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: nextMessages,
        },
        messages: nextLiveMessages,
      };
    });
  },

  setOnlineUsers: (users) => set({ onlineUsers: Array.isArray(users) ? users : [] }),

  handleUserOnline: (userId) => {
    if (!userId) return;
    set((state) => ({
      onlineUsers: state.onlineUsers.includes(userId) ? state.onlineUsers : [...state.onlineUsers, userId],
    }));
  },

  handleUserOffline: (userId) => {
    if (!userId) return;
    set((state) => ({
      onlineUsers: state.onlineUsers.filter((id) => id !== userId),
    }));
  },

  setTypingStatus: (conversationId, userId, isTyping) => {
    if (!conversationId || !userId) return;
    set((state) => {
      const currentTyping = state.typingUsers[conversationId] || [];
      const nextTyping = isTyping
        ? [...new Set([...currentTyping, userId])]
        : currentTyping.filter((id) => id !== userId);
      return {
        typingUsers: { ...state.typingUsers, [conversationId]: nextTyping },
      };
    });
  },

  clearTypingForConversation: (conversationId) => {
    if (!conversationId) return;
    set((state) => {
      const nextTyping = { ...state.typingUsers };
      delete nextTyping[conversationId];
      return { typingUsers: nextTyping };
    });
  },

  getTypingUsers: (conversationId) => (get().typingUsers[conversationId] || []),

  fetchConversations: async () => {
    try {
      const { data } = await apiClient.get('/chat/conversations');
      get().setConversations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching conversations', error);
    }
  },

  fetchMessages: async (conversationId) => {
    if (!conversationId) return [];
    try {
      const response = await apiClient.get(`/chat/messages/${conversationId}`);
      const payload = response.data;
      const list = Array.isArray(payload?.messages) ? payload.messages : (Array.isArray(payload) ? payload : []);
      get().setMessages(conversationId, list);
      return list;
    } catch (error) {
      console.error('Error fetching messages', error);
      return [];
    }
  },

  sendMessage: async (conversationId, content, options = {}) => {
    if (!content) return { success: false };
    try {
      const { currentUser } = useAuthStore.getState();
      const conversation = conversationId
        ? get().conversations.find((conv) => conv?._id === conversationId)
        : null;
      const inferredRecipient = conversation?.participants?.find((p) => p?._id !== currentUser?._id);
      const recipientId = options.recipientId || inferredRecipient?._id;
      if (!recipientId) {
        toast.error('No se pudo identificar el destinatario.');
        return { success: false };
      }

      const payload = {
        recipientId,
        content,
        type: options.type || 'text',
      };

      const response = await apiClient.post('/chat/send', payload);
      const message = response.data?.data || response.data?.message || response.data?.data?.message || response.data?.data || response.data;
      if (message) {
        const targetConversationId = message.conversationId || conversationId;
        if (targetConversationId) {
          get().addMessage(targetConversationId, message);
        }
      }
      return { success: true, message };
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo enviar el mensaje.');
      return { success: false, error };
    }
  },

  forwardProfile: async (profileUserId, recipientIds) => {
    try {
      const response = await apiClient.post('/chat/forward-profile', {
        profileUserId,
        recipientIds,
      });
      toast.success('Perfil enviado con exito.');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo enviar el perfil.');
      throw error;
    }
  },

  fetchLiveStreams: async (username) => {
    try {
      if (!username) {
        set({ liveStreams: [], currentLiveStream: null });
        return [];
      }
      const response = await apiClient.get(`/lives/${username}`);
      const list = Array.isArray(response.data) ? response.data : [];
      set({ liveStreams: list, currentLiveStream: list[0] || null });
      return list;
    } catch (error) {
      console.error('Error fetching live streams', error);
      set({ liveStreams: [], currentLiveStream: null });
      return [];
    }
  },

  startLiveStream: async (streamData) => {
    const stream = streamData || null;
    if (!stream) return null;
    set((state) => ({
      currentLiveStream: stream,
      isLiveStreamActive: true,
      liveStreams: state.liveStreams.some((item) => item?._id === stream?._id)
        ? state.liveStreams
        : [stream, ...state.liveStreams],
    }));
    return stream;
  },

  endLiveStream: async () => {
    set({
      currentLiveStream: null,
      isLiveStreamActive: false,
      viewerCount: 0,
      messages: [],
    });
  },

  updateViewerCount: (count) => set({ viewerCount: Number(count || 0) }),

  fetchStreamByUsername: async (username) => {
    if (!username) {
      set({ currentStream: null });
      return null;
    }
    set({ isLoading: true });
    try {
      const response = await apiClient.get(`/lives/${username}`);
      const list = Array.isArray(response.data) ? response.data : [];
      const currentStream = list[0] || null;
      set({ currentStream, isLoading: false });
      return currentStream;
    } catch (error) {
      console.error('Error fetching stream', error);
      set({ currentStream: null, isLoading: false });
      return null;
    }
  },

  setGiftAnimation: (payload) => set({ giftAnimation: payload || null }),
  clearGiftAnimation: () => set({ giftAnimation: null }),

  addReaction: (emoji) => {
    if (!emoji) return;
    const reaction = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      emoji,
      x: Math.floor(Math.random() * 90) + 5,
    };
    set((state) => ({
      reactions: [...state.reactions, reaction].slice(-50),
    }));
  },
  clearReactions: () => set({ reactions: [] }),

  initiateCall: (recipient, callType = 'video') => set({
    callStatus: 'connecting',
    callType,
    recipient,
    caller: null,
    isIncoming: false,
  }),

  receiveCall: (caller, callType = 'video') => set({
    callStatus: 'ringing',
    callType,
    caller,
    recipient: null,
    isIncoming: true,
  }),

  acceptCall: () => set({ callStatus: 'connected' }),

  setCallStatus: (status) => set({ callStatus: status }),

  endCall: () => set({
    callStatus: 'idle',
    callType: null,
    caller: null,
    recipient: null,
    isIncoming: false,
  }),
}));

export const useCallStore = useChatStore;
export const useLiveStore = useChatStore;
